export type AssistantPosition = {
  left: number;
  top: number;
};

export type AssistantSize = {
  width: number;
  height: number;
};

export type AssistantPlacement = 'anchor' | 'left-bottom' | 'bottom-align';

export type AssistantOffset = {
  x?: number;
  y?: number;
};

export type AssistantTransitionContext = {
  iframe: HTMLIFrameElement;
  signal: AbortSignal;
  reducedMotion: boolean;
  /** 注册特效结束、取消或失败时需要执行的清理 */
  onCleanup: (cleanup: () => void) => void;
};

export type AssistantTransitionEffect = (context: AssistantTransitionContext) => Animation | Promise<void> | void;

export type AssistantRobotEffect = (robot: HTMLElement) => Animation | void;

export const assistantButtonId = 'chatbot-bubble-button';
export const assistantIframeId = 'chatbot-bubble-window';
export const assistantScriptId = 'assistant_embed_script';
export const assistantButtonHideStyleId = 'unity-energy-assistant-button-hide-style';
export const assistantTransitionStartEvent = 'assistant-transition-start';
export const localAssistantOrigin = 'http://localhost:3000';
export const defaultAssistantSize: AssistantSize = { width: 96, height: 128 };

/**
 * 获取助手可能存在的 document。
 * 微前端场景下助手脚本可能挂在 rawWindow.document，因此这里同时兼容主窗口和 rawWindow。
 */
export const getAssistantDocuments = () => {
  return [document, window.rawWindow?.document].filter((item, index, list): item is Document => {
    return !!item && list.indexOf(item) === index;
  });
};

/**
 * 获取助手配置需要同步到的 window。
 * 去重是为了避免非微前端场景下 window 与 rawWindow 指向同一个对象时重复注册事件。
 */
export const getAssistantWindows = () => {
  return ([window, window.rawWindow] as (Window | undefined)[]).filter((item, index, list): item is Window => {
    return !!item && list.indexOf(item) === index;
  });
};

export const getAssistantEmbedController = () => {
  return getAssistantWindows()
    .map(assistantWindow => assistantWindow.assistantEmbed)
    .find(Boolean);
};

/**
 * 在主 document 与 rawWindow.document 中查找助手脚本生成的 DOM。
 */
export const findAssistantElement = <T extends HTMLElement>(id: string) => {
  for (const assistantDocument of getAssistantDocuments()) {
    const element = assistantDocument.getElementById(id) as T | null;
    if (element) return element;
  }
  return null;
};

/**
 * 获取助手按钮或 iframe 应该挂载到的容器。
 * 全屏时优先挂到全屏容器，避免 fixed 元素脱离当前大屏上下文。
 */
export const getElementPortalContainer = (element?: HTMLElement | null) => {
  const ownerDocument = element?.ownerDocument ?? document;
  return (
    (ownerDocument.fullscreenElement as HTMLElement | null) ??
    (document.fullscreenElement as HTMLElement | null) ??
    ownerDocument.getElementById('full_screen_box') ??
    document.getElementById('full_screen_box') ??
    ownerDocument.body
  );
};

/**
 * 隐藏助手脚本自带的默认按钮，只展示本地自定义机器人入口。
 * 使用 mountCount 避免多个 AiAssistant 实例同时存在时提前移除样式。
 */
export const installAssistantButtonHideStyle = () => {
  const createdStyles = getAssistantDocuments().map(assistantDocument => {
    let style = assistantDocument.getElementById(assistantButtonHideStyleId) as HTMLStyleElement | null;
    if (!style) {
      style = assistantDocument.createElement('style');
      style.id = assistantButtonHideStyleId;
      style.innerHTML = `
        #${assistantButtonId} {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
          z-index: 0 !important;
        }
        #${assistantButtonId} * {
          pointer-events: none !important;
        }
      `;
      assistantDocument.head.appendChild(style);
    }

    const count = Number(style.dataset.mountCount ?? 0) + 1;
    style.dataset.mountCount = String(count);
    return style;
  });

  return () => {
    createdStyles.forEach(style => {
      const nextCount = Number(style.dataset.mountCount ?? 1) - 1;
      if (nextCount > 0) {
        style.dataset.mountCount = String(nextCount);
        return;
      }
      style.remove();
    });
  };
};

/**
 * 批量写入 important 样式，用于覆盖助手脚本注入元素的内联样式。
 */
export const setImportantStyle = (element: HTMLElement, styles: Record<string, string>) => {
  Object.entries(styles).forEach(([key, value]) => {
    element.style.setProperty(key, value, 'important');
  });
};

/**
 * 批量移除内联样式属性，组件卸载时用于还原助手脚本元素。
 */
export const removeInlineStyles = (element: HTMLElement, properties: string[]) => {
  properties.forEach(property => {
    element.style.removeProperty(property);
  });
};

/**
 * 清理助手脚本默认按钮上由本组件写入的定位和隐藏样式。
 */
export const resetAssistantButtonStyle = () => {
  const button = findAssistantElement<HTMLDivElement>(assistantButtonId);
  if (!button) return;
  removeInlineStyles(button, [
    `--${assistantButtonId}-left`,
    `--${assistantButtonId}-top`,
    `--${assistantButtonId}-right`,
    `--${assistantButtonId}-bottom`,
    `--${assistantButtonId}-width`,
    `--${assistantButtonId}-height`,
    'display',
    'opacity',
    'visibility',
    'pointer-events',
    'z-index',
  ]);
};

/**
 * 清理助手 iframe 上由本组件写入的展示、居中和动画样式。
 */
export const resetAssistantIframeStyle = () => {
  const iframe = findAssistantElement<HTMLIFrameElement>(assistantIframeId);
  if (!iframe) return;
  assistantIframeAnimations.get(iframe)?.cancel();
  assistantIframeAnimations.delete(iframe);
  iframe.style.setProperty('display', 'none');
  removeInlineStyles(iframe, [
    'position',
    'top',
    'left',
    'right',
    'bottom',
    'width',
    'height',
    'max-width',
    'max-height',
    'z-index',
    'opacity',
    'pointer-events',
    'transform',
    'transition',
    'transform-origin',
    'filter',
    'clip-path',
    'will-change',
  ]);
};

/**
 * 归一化页面缩放值，非法或未传入时按 1 处理。
 */
export const getScaleValue = (scale?: number) => {
  return scale && Number.isFinite(scale) && scale > 0 ? scale : 1;
};

/**
 * 将助手位置限制在浏览器可视区域内，避免拖拽或初始定位后超出屏幕。
 */
export const clampAssistantPosition = (position: AssistantPosition, width: number, height: number) => {
  const margin = 8;
  const maxLeft = Math.max(margin, window.innerWidth - width - margin);
  const maxTop = Math.max(margin, window.innerHeight - height - margin);
  return {
    left: Math.min(Math.max(position.left, margin), maxLeft),
    top: Math.min(Math.max(position.top, margin), maxTop),
  };
};

/**
 * 根据设计稿尺寸和页面缩放计算机器人真实尺寸。
 * 未传 baseSize 时回退到锚点尺寸，锚点尺寸无效时再使用默认尺寸。
 */
export const getScaledAssistantSize = (anchorRect: DOMRect, baseSize?: AssistantSize, pageScale?: number) => {
  const scale = getScaleValue(pageScale);
  const nextSize = baseSize
    ? {
        width: baseSize.width * scale,
        height: baseSize.height * scale,
      }
    : {
        width: anchorRect.width || defaultAssistantSize.width * scale,
        height: anchorRect.height || defaultAssistantSize.height * scale,
      };

  return nextSize.width > 0 && nextSize.height > 0 ? nextSize : null;
};

/**
 * 根据锚点、定位方式、尺寸和偏移量计算助手的 viewport 坐标。
 * 返回值已完成可视区 clamp，可直接用于 fixed 定位。
 */
export const getAssistantAnchorLayout = (
  anchor: HTMLElement | null,
  placement: AssistantPlacement,
  baseSize?: AssistantSize,
  offset?: AssistantOffset,
  pageScale?: number
) => {
  if (!anchor) return null;

  const anchorRect = anchor.getBoundingClientRect();
  if (!anchorRect.width || !anchorRect.height || (!anchorRect.left && !anchorRect.top)) return null;

  const scale = getScaleValue(pageScale);
  const size = getScaledAssistantSize(anchorRect, baseSize, pageScale);
  if (!size) return null;

  const offsetX = (offset?.x ?? 0) * scale;
  const offsetY = (offset?.y ?? 0) * scale;
  let position: AssistantPosition;

  if (placement === 'left-bottom') {
    position = {
      left: anchorRect.left - offsetX - size.width,
      top: anchorRect.bottom - size.height + offsetY,
    };
  } else if (placement === 'bottom-align') {
    position = {
      left: anchorRect.left + offsetX,
      top: anchorRect.bottom - size.height + offsetY,
    };
  } else {
    position = {
      left: anchorRect.left + offsetX,
      top: anchorRect.top + offsetY,
    };
  }

  return {
    position: clampAssistantPosition(position, size.width, size.height),
    size,
  };
};

/**
 * 计算助手 iframe 的基础展示尺寸。
 * iframe 自身使用固定设计宽度，显示时通过 transform scale 适配大屏缩放。
 */
export const getAssistantIframeSize = (scale?: number) => {
  const pageScale = getScaleValue(scale);
  const baseWidth = 1200;
  const baseHeight = Math.max(320, (window.innerHeight - 260 * pageScale) / pageScale);
  return { width: baseWidth, height: baseHeight, pageScale };
};

const assistantIframeOpenAnimationDuration = 680;
const assistantIframeCloseAnimationDuration = 680;
const assistantIframeAnimations = new WeakMap<HTMLIFrameElement, Animation>();
const assistantPanelVisibilityEvent = 'chatbot-visibility-change';

const getAssistantTransferLayout = (scale?: number, target?: HTMLElement | null) => {
  const { width, height, pageScale } = getAssistantIframeSize(scale);
  const targetRect = target?.getBoundingClientRect();
  const hasVisibleTarget = Boolean(targetRect?.width && targetRect?.height);
  const targetCenterX = hasVisibleTarget ? targetRect!.left + targetRect!.width / 2 : window.innerWidth / 2;
  const targetCenterY = hasVisibleTarget ? targetRect!.top + targetRect!.height / 2 : window.innerHeight / 2;
  const targetScale = hasVisibleTarget
    ? Math.max(0.06, Math.min(targetRect!.width / width, targetRect!.height / height))
    : pageScale * 0.12;

  return {
    pageScale,
    targetScale,
    offsetX: targetCenterX - window.innerWidth / 2,
    offsetY: targetCenterY - window.innerHeight / 2,
  };
};

const getAssistantTransferTransform = (offsetX: number, offsetY: number, scale: number, rotateX = 0, rotateZ = 0) =>
  `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${scale}) perspective(1000px) rotateX(${rotateX}deg) rotateZ(${rotateZ}deg)`;

const notifyAssistantIframeVisibility = (iframe: HTMLIFrameElement, visible: boolean) => {
  iframe.contentWindow?.postMessage(
    {
      type: assistantPanelVisibilityEvent,
      payload: { visible },
    },
    '*'
  );
};

const animateAssistantTransferTarget = (target: HTMLElement | null | undefined, closing = false) => {
  if (!target) return;

  return target.animate(
    closing
      ? [
          { transform: 'scale(1)' },
          { offset: 0.68, transform: 'scale(0.96)' },
          {
            offset: 0.88,
            transform: 'scale(1.18)',
          },
          { transform: 'scale(1)' },
        ]
      : [
          { transform: 'scale(1)' },
          {
            offset: 0.16,
            transform: 'scale(1.18)',
          },
          { offset: 0.34, transform: 'scale(0.96)' },
          { transform: 'scale(1)' },
        ],
    {
      duration: closing ? assistantIframeCloseAnimationDuration : assistantIframeOpenAnimationDuration,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    }
  );
};

export const animateAssistantRobotOpen: AssistantRobotEffect = robot => {
  return animateAssistantTransferTarget(robot);
};

export const animateAssistantRobotClose: AssistantRobotEffect = robot => {
  return animateAssistantTransferTarget(robot, true);
};

/**
 * 将助手 iframe 居中显示，并写入打开态需要的尺寸、层级和 transform。
 */
export const centerAssistantIframe = (
  iframe: HTMLIFrameElement,
  scale?: number,
  transform = `translate(-50%, -50%) scale(${getScaleValue(scale)})`
) => {
  const { width, height } = getAssistantIframeSize(scale);
  setImportantStyle(iframe, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    right: 'unset',
    bottom: 'unset',
    width: `${width}px`,
    height: `${height}px`,
    'max-width': `${width}px`,
    'max-height': `${height}px`,
    'z-index': '2147483646',
    'pointer-events': 'auto',
  });
  iframe.style.setProperty('opacity', '1');
  iframe.style.setProperty('transform', transform);
  iframe.style.removeProperty('filter');
  iframe.style.setProperty('clip-path', 'inset(0 0 0 0 round 4px)');
  iframe.style.setProperty('transition', 'none');
  iframe.style.setProperty('transform-origin', 'center center');
};

/**
 * 执行 iframe 打开动画：从机器人所在位置传送到屏幕中央，并展开为完整面板。
 */
export const animateAssistantIframeOpen = (
  iframe: HTMLIFrameElement,
  scale?: number,
  source?: HTMLElement | null,
  animateRobot = true
) => {
  const previousAnimation = assistantIframeAnimations.get(iframe);
  previousAnimation?.cancel();

  const { pageScale, targetScale, offsetX, offsetY } = getAssistantTransferLayout(scale, source);
  const finalTransform = `translate(-50%, -50%) scale(${pageScale})`;
  centerAssistantIframe(iframe, scale, finalTransform);
  notifyAssistantIframeVisibility(iframe, true);

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    assistantIframeAnimations.delete(iframe);
    return;
  }

  if (animateRobot) {
    animateAssistantTransferTarget(source);
  }
  iframe.style.setProperty('will-change', 'transform, opacity, clip-path', 'important');
  const animation = iframe.animate(
    [
      {
        opacity: 0.18,
        transform: getAssistantTransferTransform(offsetX, offsetY, targetScale * 0.55),
        clipPath: 'inset(40% 36% 40% 36% round 50%)',
      },
      {
        offset: 0.24,
        opacity: 0.7,
        transform: getAssistantTransferTransform(offsetX, offsetY, targetScale * 1.2),
        clipPath: 'inset(30% 24% 30% 24% round 40%)',
      },
      {
        offset: 0.64,
        opacity: 0.95,
        transform: getAssistantTransferTransform(offsetX * 0.5, offsetY * 0.5, pageScale * 0.55),
        clipPath: 'inset(8% 2% 8% 2% round 12px)',
      },
      {
        offset: 0.94,
        opacity: 1,
        transform: finalTransform,
        clipPath: 'inset(0 0 0 0 round 4px)',
      },
      {
        opacity: 1,
        transform: finalTransform,
        clipPath: 'inset(0 0 0 0 round 4px)',
      },
    ],
    {
      duration: assistantIframeOpenAnimationDuration,
      easing: 'cubic-bezier(0.4, 0, 0.8, 1)',
      fill: 'both',
    }
  );
  assistantIframeAnimations.set(iframe, animation);
  animation.finished
    .then(() => {
      if (assistantIframeAnimations.get(iframe) !== animation) return;
      animation.cancel();
      assistantIframeAnimations.delete(iframe);
      iframe.style.removeProperty('will-change');
    })
    .catch(() => undefined);
  return animation;
};

/**
 * 执行 iframe 收起动画：将完整面板压缩成能量光点并回收到机器人位置。
 */
export const animateAssistantIframeClose = (
  iframe: HTMLIFrameElement,
  scale?: number,
  target?: HTMLElement | null,
  animateRobot = true
) => {
  const previousAnimation = assistantIframeAnimations.get(iframe);
  previousAnimation?.cancel();

  const { pageScale, targetScale, offsetX, offsetY } = getAssistantTransferLayout(scale, target);
  const finalTransform = `translate(-50%, -50%) scale(${pageScale})`;
  centerAssistantIframe(iframe, scale, finalTransform);
  iframe.style.setProperty('pointer-events', 'none', 'important');

  const finishClose = () => {
    iframe.style.setProperty('display', 'none');
    iframe.style.removeProperty('will-change');
    notifyAssistantIframeVisibility(iframe, false);
  };

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    assistantIframeAnimations.delete(iframe);
    finishClose();
    return;
  }

  if (animateRobot) {
    animateAssistantTransferTarget(target, true);
  }
  iframe.style.setProperty('will-change', 'transform, opacity, clip-path', 'important');
  const animation = iframe.animate(
    [
      {
        opacity: 1,
        transform: finalTransform,
        clipPath: 'inset(0 0 0 0 round 4px)',
      },
      {
        offset: 0.12,
        opacity: 1,
        transform: getAssistantTransferTransform(offsetX * 0.08, offsetY * 0.08, pageScale * 0.96, 1),
        clipPath: 'inset(2% 0.5% 2% 0.5% round 6px)',
      },
      {
        offset: 0.32,
        opacity: 0.86,
        transform: getAssistantTransferTransform(offsetX * 0.42, offsetY * 0.42, pageScale * 0.72, 5),
        clipPath: 'inset(9% 3% 9% 3% round 12px)',
      },
      {
        offset: 0.72,
        opacity: 0.48,
        transform: getAssistantTransferTransform(offsetX, offsetY, targetScale * 1.35, 8, 4),
        clipPath: 'inset(30% 24% 30% 24% round 40%)',
      },
      {
        opacity: 0,
        transform: getAssistantTransferTransform(offsetX, offsetY, targetScale * 0.35, 12, -8),
        clipPath: 'inset(42% 38% 42% 38% round 50%)',
      },
    ],
    {
      duration: assistantIframeCloseAnimationDuration,
      easing: 'cubic-bezier(0.4, 0, 0.8, 1)',
      fill: 'both',
    }
  );
  assistantIframeAnimations.set(iframe, animation);
  animation.finished
    .then(() => {
      if (assistantIframeAnimations.get(iframe) !== animation) return;
      animation.cancel();
      assistantIframeAnimations.delete(iframe);
      finishClose();
    })
    .catch(() => undefined);
  return animation;
};

/**
 * 将助手脚本生成的默认按钮和 iframe 同步到当前正确的 portal 容器。
 * 主要用于全屏切换后保持 iframe 和本地按钮在同一个层级上下文。
 */
export const syncAssistantPortalContainer = () => {
  const button = findAssistantElement<HTMLDivElement>(assistantButtonId);
  const iframe = findAssistantElement<HTMLIFrameElement>(assistantIframeId);
  if (button) {
    const container = getElementPortalContainer(button);
    if (button.parentElement !== container) {
      container.appendChild(button);
    }
  }
  if (iframe) {
    const container = getElementPortalContainer(iframe);
    if (iframe.parentElement !== container) {
      container.appendChild(iframe);
    }
  }
};

/**
 * 获取助手脚本应该注入到的 document。
 * 若脚本已存在，则复用它所在的 document，避免跨 document 重复注入。
 */
export const getAssistantScriptDocument = () => {
  const existingScript = findAssistantElement<HTMLScriptElement>(assistantScriptId);
  if (existingScript) return existingScript.ownerDocument;
  return document;
};

/**
 * 判断当前环境是否需要主动注入助手脚本。
 * 微前端环境通常由主应用负责注入，开发环境和非微前端环境由当前页面注入。
 */
export const shouldInjectAssistantScript = () => {
  return process.env.NODE_ENV === 'development' || !window.__MICRO_APP_ENVIRONMENT__;
};

/**
 * 获取元素的计算样式，优先使用元素所属 document 的 defaultView。
 */
export const getElementComputedStyle = (element: Element) => {
  return element.ownerDocument.defaultView?.getComputedStyle(element) ?? window.getComputedStyle(element);
};
